import { useState, useEffect, useCallback } from 'react';

const TIMESLOTS_API =
  'https://readdy.ai/api/public/calendar/timeslots/1b9ed043-e352-4e7c-847d-682a4a15111e.8d52b87b86f92aa233934e5ff1ada5d9312068e27075cb45e696a8f9d58591c4';
const FORM_URL = 'https://readdy.ai/api/form/d6dnrubv53dtlm7j8of0';

interface Timeslot {
  start_time: string;
  end_time: string;
}

interface FormState {
  customer_name: string;
  customer_phone: string;
  tipo_instalacion: string;
  direccion: string;
}

const DAYS_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTHS_ES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

const TIPO_OPTIONS = [
  'Residencial (vivienda)',
  'Empresa / Comercio',
  'Industrial / Nave',
  'Comunidad de vecinos',
  'Otro',
];

function getDateStr(dt: string): string {
  return dt.split(' ')[0];
}

function getTimeStr(dt: string): string {
  const parts = dt.split(' ');
  if (parts.length < 2) return '';
  return parts[1].substring(0, 5);
}

function getTodayStr(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseYearMonth(dateStr: string): { year: number; month: number } {
  const parts = dateStr.split('-');
  return { year: parseInt(parts[0], 10), month: parseInt(parts[1], 10) - 1 };
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function buildDateStr(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export default function BookingCalendar() {
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [timeslots, setTimeslots] = useState<Timeslot[]>([]);
  const [loading, setLoading] = useState(true);
  const [calYear, setCalYear] = useState<number>(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState<number>(new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Timeslot | null>(null);
  const [form, setForm] = useState<FormState>({
    customer_name: '',
    customer_phone: '',
    tipo_instalacion: '',
    direccion: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const fetchTimeslots = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(TIMESLOTS_API);
      const data = await res.json();
      const slots: Timeslot[] = Array.isArray(data) ? data : (data.timeslots ?? data.data ?? []);
      setTimeslots(slots);

      const today = getTodayStr();
      const availableDates = slots
        .map((s) => getDateStr(s.start_time))
        .filter((d, i, arr) => arr.indexOf(d) === i)
        .sort();

      const closestDate = availableDates.find((d) => d >= today) ?? availableDates[availableDates.length - 1];
      if (closestDate) {
        const { year, month } = parseYearMonth(closestDate);
        setCalYear(year);
        setCalMonth(month);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTimeslots();
  }, [fetchTimeslots]);

  const availableDatesSet = new Set(timeslots.map((s) => getDateStr(s.start_time)));

  const slotsForDate = selectedDate
    ? timeslots.filter((s) => getDateStr(s.start_time) === selectedDate)
    : [];

  const daysInMonth = getDaysInMonth(calYear, calMonth);
  const firstDay = getFirstDayOfMonth(calYear, calMonth);

  const prevMonth = () => {
    if (calMonth === 0) { setCalYear((y) => y - 1); setCalMonth(11); }
    else setCalMonth((m) => m - 1);
  };

  const nextMonth = () => {
    if (calMonth === 11) { setCalYear((y) => y + 1); setCalMonth(0); }
    else setCalMonth((m) => m + 1);
  };

  const handleDateSelect = (dateStr: string) => {
    if (!availableDatesSet.has(dateStr)) return;
    setSelectedDate(dateStr);
    setStep(1);
  };

  const handleSlotSelect = (slot: Timeslot) => {
    setSelectedSlot(slot);
    setStep(2);
  };

  const handleModifyTime = () => {
    setStep(0);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot) return;
    setSubmitting(true);
    setSubmitStatus('idle');

    try {
      const body = new URLSearchParams();
      body.append('nombre', form.customer_name);
      body.append('telefono', form.customer_phone);
      body.append('email', 'cita@jyafotovoltaica.com');
      body.append('servicio', `Visita presupuesto - ${form.tipo_instalacion || 'No especificado'}`);
      body.append('provincia', form.direccion);
      body.append('mensaje', `NUEVA CITA AGENDADA\n\nFecha: ${getDateStr(selectedSlot.start_time)}\nHorario: ${getTimeStr(selectedSlot.start_time)} - ${getTimeStr(selectedSlot.end_time)}\n\nCliente: ${form.customer_name}\nTeléfono: ${form.customer_phone}\nTipo de instalación: ${form.tipo_instalacion || 'No especificado'}\nDirección: ${form.direccion || 'No especificada'}`);

      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (res.ok) {
        setSubmitStatus('success');
        await fetchTimeslots();
        setSelectedDate(null);
        setSelectedSlot(null);
        setForm({ customer_name: '', customer_phone: '', tipo_instalacion: '', direccion: '' });
        setStep(0);
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setSubmitting(false);
    }
  };

  const calendarCells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) calendarCells.push(null);
  for (let d = 1; d <= daysInMonth; d++) calendarCells.push(d);

  const today = getTodayStr();

  return (
    <div className="w-full">
      {/* Step indicator */}
      <div className="flex items-center justify-center gap-2 mb-8">
        {(['Fecha', 'Horario', 'Confirmar'] as const).map((label, idx) => (
          <div key={label} className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all ${
              step === idx
                ? 'bg-[#FFB800] text-gray-900'
                : step > idx
                ? 'bg-[#0A2E1F] text-white'
                : 'bg-gray-200 text-gray-500'
            }`}>
              {step > idx ? <i className="ri-check-line text-sm"></i> : idx + 1}
            </div>
            <span className={`text-sm font-medium hidden sm:block ${step === idx ? 'text-gray-900' : 'text-gray-400'}`}>{label}</span>
            {idx < 2 && <div className={`w-8 h-0.5 ${step > idx ? 'bg-[#0A2E1F]' : 'bg-gray-200'}`}></div>}
          </div>
        ))}
      </div>

      {/* STEP 0: Calendar */}
      {step === 0 && (
        <div>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line animate-spin text-3xl text-[#FFB800]"></i>
            </div>
          ) : (
            <div className="max-w-md mx-auto">
              {/* Month nav */}
              <div className="flex items-center justify-between mb-6">
                <button
                  onClick={prevMonth}
                  className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-xl text-gray-600"></i>
                </button>
                <span className="text-base font-semibold text-gray-900">
                  {MONTHS_ES[calMonth]} {calYear}
                </span>
                <button
                  onClick={nextMonth}
                  className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-xl text-gray-600"></i>
                </button>
              </div>

              {/* Day headers */}
              <div className="grid grid-cols-7 mb-2">
                {DAYS_ES.map((d) => (
                  <div key={d} className="text-center text-xs font-semibold text-gray-400 py-1">{d}</div>
                ))}
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {calendarCells.map((day, idx) => {
                  if (day === null) return <div key={`empty-${idx}`} />;
                  const dateStr = buildDateStr(calYear, calMonth, day);
                  const isAvailable = availableDatesSet.has(dateStr);
                  const isPast = dateStr < today;
                  const isSelected = selectedDate === dateStr;
                  const isToday = dateStr === today;

                  return (
                    <button
                      key={dateStr}
                      onClick={() => handleDateSelect(dateStr)}
                      disabled={!isAvailable || isPast}
                      className={`
                        relative aspect-square flex flex-col items-center justify-center rounded-xl text-sm font-medium transition-all
                        ${isSelected ? 'bg-[#FFB800] text-gray-900 font-bold' : ''}
                        ${isAvailable && !isPast && !isSelected ? 'bg-[#0A2E1F]/10 text-[#0A2E1F] hover:bg-[#FFB800]/80 hover:text-gray-900 cursor-pointer' : ''}
                        ${(!isAvailable || isPast) && !isSelected ? 'text-gray-300 cursor-not-allowed' : ''}
                        ${isToday && !isSelected ? 'ring-2 ring-[#FF6B35]' : ''}
                      `}
                    >
                      <span>{day}</span>
                      {isAvailable && !isPast && (
                        <span className={`w-1 h-1 rounded-full mt-0.5 ${isSelected ? 'bg-gray-900' : 'bg-[#FFB800]'}`}></span>
                      )}
                    </button>
                  );
                })}
              </div>

              <p className="text-xs text-gray-400 text-center mt-4">
                Las fechas con punto naranja tienen disponibilidad
              </p>
            </div>
          )}
        </div>
      )}

      {/* STEP 1: Time slot selection */}
      {step === 1 && selectedDate && (
        <div className="max-w-md mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <button
              onClick={() => setStep(0)}
              className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line text-gray-600"></i>
            </button>
            <div>
              <p className="text-xs text-gray-400">Fecha seleccionada</p>
              <p className="text-base font-semibold text-gray-900">{selectedDate}</p>
            </div>
          </div>

          <h3 className="text-sm font-semibold text-gray-700 mb-4">Elige un horario disponible</h3>

          {slotsForDate.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-8">No hay horarios disponibles para esta fecha.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {slotsForDate.map((slot, i) => (
                <button
                  key={i}
                  onClick={() => handleSlotSelect(slot)}
                  className={`border-2 rounded-xl px-4 py-3 text-sm font-medium transition-all cursor-pointer
                    ${selectedSlot?.start_time === slot.start_time
                      ? 'border-[#FFB800] bg-[#FFB800]/10 text-gray-900'
                      : 'border-gray-200 hover:border-[#FFB800] hover:bg-[#FFB800]/5 text-gray-700'
                    }`}
                >
                  <div className="flex items-center gap-2 justify-center">
                    <i className="ri-time-line text-[#FF6B35]"></i>
                    <span>{getTimeStr(slot.start_time)} – {getTimeStr(slot.end_time)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* STEP 2: Review + Form + Submit */}
      {step === 2 && selectedSlot && (
        <div className="max-w-lg mx-auto">
          {submitStatus === 'success' ? (
            <div className="text-center py-10">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                <i className="ri-check-line text-green-600 text-4xl"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">¡Visita agendada!</h3>
              <p className="text-gray-500 text-sm mb-6 max-w-xs mx-auto">
                Hemos confirmado tu visita de presupuesto. Recibirás un email de confirmación con todos los detalles.
              </p>
              <button
                onClick={() => { setSubmitStatus('idle'); setStep(0); }}
                className="bg-[#0A2E1F] text-white px-8 py-3 rounded-lg text-sm font-semibold hover:bg-[#FFB800] hover:text-gray-900 transition-all cursor-pointer whitespace-nowrap"
              >
                Agendar otra visita
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              {/* Review block */}
              <div className="bg-[#0A2E1F]/5 border border-[#0A2E1F]/10 rounded-2xl p-5 mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-gray-700">Tu cita seleccionada</h4>
                  <button
                    type="button"
                    onClick={handleModifyTime}
                    className="text-xs text-[#FF6B35] font-medium hover:underline cursor-pointer whitespace-nowrap"
                  >
                    Cambiar horario
                  </button>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#FFB800] rounded-xl flex-shrink-0">
                    <i className="ri-calendar-check-line text-gray-900 text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{getDateStr(selectedSlot.start_time)}</p>
                    <p className="text-xs text-gray-500">
                      {getTimeStr(selectedSlot.start_time)} – {getTimeStr(selectedSlot.end_time)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Form fields */}
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Nombre completo <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="customer_name"
                      type="text"
                      required
                      value={form.customer_name}
                      onChange={handleFormChange}
                      placeholder="Tu nombre y apellidos"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FFB800]/40 focus:border-[#FFB800] transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Teléfono <span className="text-red-500">*</span>
                    </label>
                    <input
                      name="customer_phone"
                      type="tel"
                      required
                      value={form.customer_phone}
                      onChange={handleFormChange}
                      placeholder="+34 600 000 000"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FFB800]/40 focus:border-[#FFB800] transition-all"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Tipo de instalación
                  </label>
                  <select
                    name="tipo_instalacion"
                    value={form.tipo_instalacion}
                    onChange={handleFormChange}
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FFB800]/40 focus:border-[#FFB800] transition-all appearance-none bg-white cursor-pointer"
                  >
                    <option value="">Selecciona una opción</option>
                    {TIPO_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Dirección de la visita
                  </label>
                  <input
                    name="direccion"
                    type="text"
                    value={form.direccion}
                    onChange={handleFormChange}
                    placeholder="Calle, número, municipio..."
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#FFB800]/40 focus:border-[#FFB800] transition-all"
                  />
                </div>
              </div>

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mt-4 flex items-center gap-2">
                  <i className="ri-error-warning-line text-lg"></i>
                  Ha ocurrido un error. Por favor, inténtalo de nuevo.
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full mt-6 bg-[#FF6B35] text-white py-3.5 rounded-lg text-sm font-semibold hover:bg-[#FFB800] hover:text-gray-900 transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-lg"></i>
                    Confirmando...
                  </>
                ) : (
                  <>
                    Confirmar visita de presupuesto
                    <i className="ri-calendar-check-line text-lg"></i>
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
